export const TASK_QUEUES = [
  "fallido",
  "firma",
  "reserva",
  "pago",
  "docs",
  "comunicar",
  "justificante",
  "error",
  "revision",
  "duplicado",
  "condicional",
  "hub",
  "presupuesto",
  "propuesta_caducada",
  "propuesta_aceptada",
] as const;

export type TaskQueue = (typeof TASK_QUEUES)[number];

export const TASK_DOMAINS = [
  "contracts",
  "investments",
  "audit",
  "investors",
  "promoters",
  "proposals",
] as const;

export type TaskDomain = (typeof TASK_DOMAINS)[number];

export const TASK_URGENCIES = ["urgent", "normal"] as const;

export type TaskUrgency = (typeof TASK_URGENCIES)[number];

export type TaskTone = "destructive" | "warning" | "neutral";

export type TaskObjectType =
  | "contract"
  | "investment"
  | "document"
  | "classification"
  | "investor"
  | "fiscal_quota"
  | "investment_proposal";

export const isTaskQueue = (value: string): value is TaskQueue =>
  TASK_QUEUES.some((queue) => queue === value);

export const AUDIT_QUEUES: readonly TaskQueue[] = ["error", "revision", "duplicado", "condicional"];

export interface TaskObject {
  type: TaskObjectType;
  uuid: string;
  label: string;
}

export interface TaskQuotaSummary {
  uuid: string;
  fiscal_year: number;
  incentive_type: string | null;
  project_titles: string[];
}

export interface TaskContext {
  investment_uuid: string | null;
  document_uuid: string | null;
  classification_uuid: string | null;
  investor_uuid: string | null;
  beneficiary_uuid: string | null;
  investment_proposal_uuid: string | null;
  fiscal_quota: TaskQuotaSummary | null;
}

export interface TaskAssignee {
  uuid: string;
  name: string;
  email: string;
}

export interface PendingItem {
  code: string;
  queue: TaskQueue;
  domain: TaskDomain;
  tone: TaskTone;
  urgency: TaskUrgency;
  object: TaskObject;
  secondary: string | null;
  due_at: string | null;
  since_at: string | null;
  amount: string | null;
  count: number | null;
  context: TaskContext;
  assignee: TaskAssignee | null;
  snooze_expires_at: string | null;
}

export interface PendingItemState {
  code: string;
  queue: TaskQueue;
  object_uuid: string;
  assignee_uuid: string | null;
  snooze_expires_at: string | null;
  dismissed_at: string | null;
}

export interface PendingQueueSummary {
  queue: TaskQueue;
  domain: TaskDomain;
  tone: TaskTone;
  total: number;
  urgent: number;
}

export interface PendingSummary {
  queues: PendingQueueSummary[];
  pending_items: number;
  urgent_items: number;
}

export type TaskSortField = "assignee" | "domain" | "due_at" | "queue" | "urgency";

export interface TaskSort {
  key: TaskSortField;
  direction: "asc" | "desc";
}

export interface TaskListFilters {
  search?: string;
  domain?: TaskDomain[];
  queue?: TaskQueue[];
  urgency?: TaskUrgency[];
  assignee?: string[];
  due_from?: string;
  due_to?: string;
  include?: string[];
}

export interface TaskListParams extends TaskListFilters {
  limit?: number;
  offset?: number;
  sort?: readonly TaskSort[];
}

export interface TaskListResponse {
  items: PendingItem[];
  total_count: number;
  total_unfiltered_count: number;
  limit: number;
  offset: number;
}

export const TASK_AGGREGATE_FUNCTIONS = {
  queue: ["distribution"],
  domain: ["distribution"],
  urgency: ["distribution"],
  assignee: ["distribution", "unique", "filled", "empty"],
  amount: ["sum", "avg", "min", "max"],
} as const;

type TaskAggregateField = keyof typeof TASK_AGGREGATE_FUNCTIONS;

type AggregatePair<F extends TaskAggregateField> = F extends TaskAggregateField
  ? `${F}:${(typeof TASK_AGGREGATE_FUNCTIONS)[F][number]}`
  : never;

export type TaskAggregateKey = AggregatePair<TaskAggregateField>;

export type TaskAggregateValue = number | string | Record<string, number> | null;

export type TaskAggregates = Partial<Record<TaskAggregateKey, TaskAggregateValue>>;

export interface TaskAggregatesParams extends TaskListFilters {
  aggregates: readonly TaskAggregateKey[];
}

export const UNASSIGNED_KEY = "none";

export const ASSIGNEE_ME = "me";
